import { useEffect, useState } from 'preact/hooks';
import { ApiError, chooseRetention, getPlan, type RetentionApi, type RetentionFileApi } from '../lib/api';
import './RetentionChooser.css';

// F3 — THE PARENT CHOOSES. (plan of record `spec-calltowrap-feature-complete-
// the-18-packages-and-the-plan-of-record-2026-08`, plan model §3)
//
// ===================================================================
// ⚑ ONLY A PERSON DESTROYS A FILE, NEVER A CLOCK
// ===================================================================
// This component exists because the clock is not allowed to act. A file that
// has reached the window is not gone, not hidden, not degraded — it is
// exactly where it was, waiting, and the only thing that changed is that
// this surface appeared. Everything below is written so a parent can read
// that in three seconds.
//
// ===================================================================
// THE REGISTER: SPACE REUSED, NEVER MATERIAL TAKEN
// ===================================================================
// A retention notice is the easiest place in a product to frighten somebody.
// So the copy here never says "expire", never says "we remove", and never
// leads with a countdown. It says what is true: the room these older files
// are holding can go to new auditions if the family wants it to, and if they
// would rather keep them, keeping them is one tap and costs nothing.
//
// And it says the other true thing OUT LOUD, because it is the thing a
// worried parent actually wants to know: none of this touches recording. The
// app, the uploads, the captures all keep working whatever they choose.
//
// ===================================================================
// THREE DOORS, AND THE ONE THAT CAN BE SHUT
// ===================================================================
// Upgrade · Recycle · Clear. Recycle is refused when the household is at its
// storage cap, and the refusal is NAMED where the button was — another
// window with no room to record into is a trap, not a kindness. It is never
// silently hidden: a door that is closed and says why is honest; a door that
// vanishes teaches a parent the product is unpredictable.

export interface RetentionChooserProps {
  tenantId: string;
  retention: RetentionApi;
  /** The parent moved to a plan question — the app decides where that goes. */
  onUpgrade: () => void;
  /** Something actually changed on the server; the household must re-read. */
  onChanged: () => void;
  onClose: () => void;
}

type Mode = 'doors' | 'clearing';

interface Receipt {
  line: string;
  detail?: string;
}

const messageOf = (err: unknown): string =>
  err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err);

/** Bytes as a family reads them — the same shape Settings' storage meter
 * uses, so the two numbers on the two screens look like one number. */
export function formatBytes(bytes: number): string {
  const gb = bytes / (1024 * 1024 * 1024);
  if (gb >= 1) return `${gb >= 100 ? Math.round(gb) : Math.round(gb * 10) / 10} GB`;
  const mb = bytes / (1024 * 1024);
  return `${Math.max(1, Math.round(mb))} MB`;
}

/** The day a file reaches the window, as a person says it. Derived from the
 * file's own anchor and the household's own retention, never guessed. */
export function dueDateOf(file: RetentionFileApi, retentionDays: number): string {
  const due = new Date(new Date(file.anchorAt).getTime() + retentionDays * 86_400_000);
  if (Number.isNaN(due.getTime())) return '';
  return due.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
}

function labelOf(file: RetentionFileApi): string {
  if (file.originalFilename && file.originalFilename.trim()) return file.originalFilename.trim();
  if (file.mediaClass) return file.mediaClass;
  return 'File';
}

export function RetentionChooser({ tenantId, retention, onUpgrade, onChanged, onClose }: RetentionChooserProps) {
  const [mode, setMode] = useState<Mode>('doors');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  // ⚑ IS THERE A PLAN TO MOVE TO AT ALL? `billing.available` is false on every
  // deployment with no Stripe — which is all of them until the keys are minted
  // — and on those the Plan section of Settings does not exist. "Keep
  // everything → A plan that never asks again" then sends a parent to a screen
  // with no plan on it, which is a dead end wearing the clothes of an answer.
  // Null means unread: the door stays, because removing it on a guess would be
  // its own defect.
  const [billingAvailable, setBillingAvailable] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    void getPlan(tenantId)
      .then((p) => {
        if (!cancelled) setBillingAvailable(p.billing.available);
      })
      .catch(() => {
        // Unknown stays unknown, and unknown keeps the door. It is only ever
        // removed on a positive "nothing is for sale here", never on a read
        // that failed — a parent who could upgrade must not lose the offer to
        // a dropped request.
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);
  // The batch a parent is deciding about: everything that has reached the
  // window. Warned files are shown as a badge elsewhere and are NOT part of
  // the batch — asking someone to decide about a file that is not due yet is
  // how a courtesy becomes a nag.
  const due = retention.files.filter((f) => f.state === 'due');
  const [picked, setPicked] = useState<Set<string>>(new Set(due.map((f) => f.fileId)));

  const dueBytes = due.reduce((sum, f) => sum + f.sizeBytes, 0);
  const pickedFiles = due.filter((f) => picked.has(f.fileId));
  const pickedBytes = pickedFiles.reduce((sum, f) => sum + f.sizeBytes, 0);

  async function recycle() {
    setBusy(true);
    setError(null);
    try {
      const result = await chooseRetention(tenantId, { choice: 'recycle', fileIds: due.map((f) => f.fileId) });
      setReceipt({
        line: `Kept. ${result.recycledFileIds?.length ?? 0} files stay another ${retention.retentionDays} days.`,
        detail: 'Nothing moved and nothing was removed.',
      });
      onChanged();
    } catch (err) {
      // The refusal that has its own answer. Everything else says what the
      // server said — never "something went wrong".
      if (err instanceof ApiError && err.code === 'recycle_over_quota') {
        setError(
          `There is no room left — ${formatBytes(retention.storage.usedBytes)} of ${formatBytes(retention.storage.capBytes)} is in use. ` +
            'Moving to a bigger plan makes room, or clear some space below.'
        );
      } else {
        setError(messageOf(err));
      }
    } finally {
      setBusy(false);
    }
  }

  async function clear() {
    if (pickedFiles.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const result = await chooseRetention(tenantId, {
        choice: 'delete',
        fileIds: pickedFiles.map((f) => f.fileId),
        // ⚑ THE CONFIRMATION THE SERVER CAN SEE. The parent ticked the list
        // and pressed the second button; this is that act, made explicit on
        // the wire so no retry or second client can clear a shelf by
        // omission.
        confirm: true,
      });
      setReceipt({
        line: `${result.deletedFileIds?.length ?? 0} files cleared — ${formatBytes(result.freedBytes ?? 0)} free for new auditions.`,
        detail: `${formatBytes(result.storage.usedBytes)} of ${formatBytes(result.storage.capBytes)} in use now.`,
      });
      setMode('doors');
      onChanged();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }

  function toggle(fileId: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  }

  // The receipt outlives the act. A parent who just cleared 4 GB should read
  // what happened, not watch the surface vanish — the pattern AddPerformer
  // paid for.
  if (receipt) {
    return (
      <div class="rt">
        <div class="rt__receipt" role="status">
          <div class="rt__receipt-line">{receipt.line}</div>
          {receipt.detail ? <div class="rt__receipt-detail">{receipt.detail}</div> : null}
        </div>
        <div class="rt__acts">
          <button type="button" class="rt__cancel" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div class="rt">
      <div class="rt__head">
        <span class="rt__count">
          {due.length} {due.length === 1 ? 'file' : 'files'} · {formatBytes(dueBytes)}
        </span>
        <span class="rt__age">past {retention.retentionDays} days</span>
      </div>

      <p class="rt__say">
        These are still here, exactly as you left them. Nothing is removed for you — the room they
        hold is yours to keep or to reuse for new auditions.
      </p>
      <p class="rt__say rt__say--soft">
        Whatever you choose, recording carries on as it is: this only ever concerns the older files.
      </p>

      {error ? (
        <div class="rt__error" role="status">
          {error}
        </div>
      ) : null}

      {mode === 'doors' ? (
        <div class="rt__doors">
          {billingAvailable === false ? null : (
            <button type="button" class="rt__door" onClick={onUpgrade} disabled={busy}>
              <span class="rt__door-k">Keep everything</span>
              <span class="rt__door-v">A plan that never asks again</span>
            </button>
          )}

          <button
            type="button"
            class="rt__door"
            onClick={() => void recycle()}
            disabled={busy || !retention.canRecycle}
          >
            <span class="rt__door-k">Keep these</span>
            <span class="rt__door-v">
              {retention.canRecycle
                ? `Another ${retention.retentionDays} days`
                : billingAvailable === false
                  ? 'No room left — clearing some space makes room'
                  : 'No room left — a bigger plan, or clear some space'}
            </span>
          </button>

          <button type="button" class="rt__door" onClick={() => setMode('clearing')} disabled={busy}>
            <span class="rt__door-k">Clear the space</span>
            <span class="rt__door-v">{formatBytes(dueBytes)} back for new auditions</span>
          </button>
        </div>
      ) : (
        <div class="rt__clear">
          <div class="rt__list">
            {due.map((file) => (
              <label key={file.fileId} class="rt__item">
                <input
                  type="checkbox"
                  checked={picked.has(file.fileId)}
                  onChange={() => toggle(file.fileId)}
                  disabled={busy}
                />
                <span class="rt__item-name">{labelOf(file)}</span>
                <span class="rt__item-meta">
                  {formatBytes(file.sizeBytes)} · {file.ageDays} days
                </span>
              </label>
            ))}
          </div>
          <div class="rt__acts">
            {/* The second press, and it names the number. "Clear 6 files ·
                420 MB" is a sentence a person can check against what they
                ticked; "Confirm" is not. */}
            <button
              type="button"
              class="rt__go"
              onClick={() => void clear()}
              disabled={busy || pickedFiles.length === 0}
            >
              {busy
                ? 'Clearing…'
                : `Clear ${pickedFiles.length} ${pickedFiles.length === 1 ? 'file' : 'files'} · ${formatBytes(pickedBytes)}`}
            </button>
            <button type="button" class="rt__cancel" onClick={() => setMode('doors')} disabled={busy}>
              Back
            </button>
          </div>
        </div>
      )}

      {mode === 'doors' ? (
        <div class="rt__acts">
          <button type="button" class="rt__cancel" onClick={onClose} disabled={busy}>
            Not now
          </button>
        </div>
      ) : null}
    </div>
  );
}
